import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsRotate,
  faFileExport,
  faFileImport,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";

const STORAGE_KEY_SHEET = (wsId) => `syncStock_selectedSheet_${wsId}`;
const STORAGE_KEY_SHEET_NAME = (wsId) => `syncStock_selectedSheetName_${wsId}`;

const SyncStockTab = ({ workspaceId }) => {
  // Sheet selection
  const [sheetFiles, setSheetFiles] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(
    () => localStorage.getItem(STORAGE_KEY_SHEET(workspaceId)) || ""
  );
  const [sheetNames, setSheetNames] = useState([]);
  const [selectedSheetName, setSelectedSheetName] = useState(
    () => localStorage.getItem(STORAGE_KEY_SHEET_NAME(workspaceId)) || "SKU-supplier"
  );
  const [loadingSheetNames, setLoadingSheetNames] = useState(false);

  // Export
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState(null);

  // Sync
  const [skusText, setSkusText] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState([]);

  // Fetch connected Google Sheets files from settings
  useEffect(() => {
    const fetchSheetFiles = async () => {
      try {
        const optRes = await Utils._apiRequest({
          path: Enums.endpoints.options.LIST,
          type: "GET",
        });
        
        if (optRes && Array.isArray(optRes.data)) {
          const sheets = optRes.data.filter(
            (item) => item.group === "tiktok_lising_sheets"
          );
          setSheetFiles(sheets);
          if (sheets.length > 0 && !selectedSheet) {
            const saved = localStorage.getItem(STORAGE_KEY_SHEET(workspaceId));
            const url = saved || sheets[0].values.url;
            setSelectedSheet(url);
            if (!saved) localStorage.setItem(STORAGE_KEY_SHEET(workspaceId), url);
          }
        }
      } catch (error) {
        console.log("Failed to fetch sheet files:", error.message);
      }
    };
    fetchSheetFiles();
  }, []);

  // Fetch sheet tab names when a spreadsheet is selected
  const fetchSheetNames = useCallback(async () => {
    if (!selectedSheet) return;
    setLoadingSheetNames(true);
    try {
      const data = await Utils._apiRequest({
        path: Enums.endpoints.supplierStock.SHEET_NAMES,
        type: "GET",
        query: { spreadsheetUrl: selectedSheet },
      });
      if (data.data) {
        setSheetNames(data.data);
        const saved = localStorage.getItem(STORAGE_KEY_SHEET_NAME(workspaceId));
        const match = saved && data.data.find((s) => s.title === saved);
        if (match) {
          setSelectedSheetName(match.title);
        } else {
          // Auto-select "SKU-supplier" if exists, else first
          const defaultTab = data.data.find((s) => s.title === "SKU-supplier");
          const name = defaultTab ? defaultTab.title : data.data[0]?.title || "";
          setSelectedSheetName(name);
          if (name) localStorage.setItem(STORAGE_KEY_SHEET_NAME(workspaceId), name);
        }
      }
    } catch (error) {
      toast.error("Không thể tải danh sách sheet: " + error.message);
    }
    setLoadingSheetNames(false);
  }, [selectedSheet]);

  useEffect(() => {
    fetchSheetNames();
  }, [fetchSheetNames]);

  // Export stock status to sheet
  const handleExport = async () => {
    if (!selectedSheet || !selectedSheetName) {
      toast.warning("Vui lòng chọn file và sheet name");
      return;
    }
    setExporting(true);
    setExportResult(null);
    try {
      const data = await Utils._apiRequest({
        path: Enums.endpoints.supplierStock.SHEET_EXPORT_STOCK,
        type: "POST",
        payload: {
          spreadsheetUrl: selectedSheet,
          sheetName: selectedSheetName,
          workspace: workspaceId,
        },
      });
      if (data.errors) {
        toast.error(data.errors);
      } else {
        setExportResult(data);
        toast.success(data.message || "Xuất thành công!");
      }
    } catch (error) {
      toast.error("Lỗi: " + error.message);
    }
    setExporting(false);
  };

  // Sync stock from sheet to server
  const handleSync = async () => {
    if (!selectedSheet || !selectedSheetName) {
      toast.warning("Vui lòng chọn file và sheet name");
      return;
    }
    if (!skusText.trim()) {
      toast.warning("Vui lòng nhập danh sách SKU");
      return;
    }
    setSyncing(true);
    setSyncResults([]);
    try {
      const data = await Utils._apiRequest({
        path: Enums.endpoints.supplierStock.SHEET_SYNC_STOCK,
        type: "POST",
        payload: {
          spreadsheetUrl: selectedSheet,
          sheetName: selectedSheetName,
          workspace: workspaceId,
          skus: skusText,
        },
      });
      if (data.errors) {
        toast.error(data.errors);
      } else {
        setSyncResults(data.data || []);
        toast.success(data.message || "Sync thành công!");
      }
    } catch (error) {
      toast.error("Lỗi: " + error.message);
    }
    setSyncing(false);
  };

  const getStatusBadge = (status) => {
    const map = {
      updated: { bg: "success", text: "Đã cập nhật" },
      not_found_in_sheet: { bg: "warning", text: "Không tìm thấy trên sheet" },
      product_not_found: { bg: "danger", text: "Sản phẩm không tồn tại" },
      no_supplier_data: { bg: "secondary", text: "Không có dữ liệu supplier" },
    };
    const info = map[status] || { bg: "secondary", text: status };
    return <Badge bg={info.bg}>{info.text}</Badge>;
  };

  return (
    <div className="sync-stock-tab">
      {/* Sheet Selection */}
      <Row className="mb-4">
        <Col md={5}>
          <Form.Group>
            <Form.Label className="fw-semibold">Google Sheet File</Form.Label>
            <Form.Select
              value={selectedSheet}
              onChange={(e) => {
                setSelectedSheet(e.target.value);
                localStorage.setItem(STORAGE_KEY_SHEET(workspaceId), e.target.value);
              }}
            >
              <option value="">-- Chọn file --</option>
              {sheetFiles.map((file, i) => (
                <option key={i} value={file.values.url}>
                  {file.values.name}
                </option>
              ))}
            </Form.Select>
            {sheetFiles.length === 0 && (
              <Form.Text className="text-muted">
                Chưa có file nào. Vui lòng kết nối trong{" "}
                <a href="/settings">Cài đặt Google Sheets</a>.
              </Form.Text>
            )}
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label className="fw-semibold">Sheet Name</Form.Label>
            <div className="d-flex align-items-center gap-2">
              <Form.Select
                value={selectedSheetName}
                onChange={(e) => {
                setSelectedSheetName(e.target.value);
                localStorage.setItem(STORAGE_KEY_SHEET_NAME(workspaceId), e.target.value);
              }}
                disabled={loadingSheetNames || sheetNames.length === 0}
              >
                {loadingSheetNames ? (
                  <option>Đang tải...</option>
                ) : sheetNames.length === 0 ? (
                  <option value="">-- Không có sheet --</option>
                ) : (
                  sheetNames.map((s, i) => (
                    <option key={i} value={s.title}>
                      {s.title}
                    </option>
                  ))
                )}
              </Form.Select>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={fetchSheetNames}
                disabled={loadingSheetNames || !selectedSheet}
                title="Tải lại danh sách sheet"
              >
                <FontAwesomeIcon
                  icon={faArrowsRotate}
                  spin={loadingSheetNames}
                />
              </Button>
            </div>
          </Form.Group>
        </Col>
      </Row>

      <hr />

      {/* Feature 1: Export stock status to sheet */}
      <div className="mb-4">
        <h6 className="fw-bold mb-2">
          <FontAwesomeIcon icon={faFileExport} className="me-2 text-primary" />
          Xuất Stock Status ra Sheet
        </h6>
        <p className="text-muted small mb-2">
          Đọc danh sách SKU từ sheet, lấy stock status từ server và ghi vào cột
          "Stock status" trên sheet.
        </p>
        <Button
          variant="primary"
          onClick={handleExport}
          disabled={exporting || !selectedSheet || !selectedSheetName}
        >
          {exporting ? (
            <>
              <Spinner size="sm" className="me-2" />
              Đang xuất...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faFileExport} className="me-2" />
              Xuất Stock Status
            </>
          )}
        </Button>
        {exportResult && (
          <Alert variant="success" className="mt-2 py-2">
            {exportResult.message}
          </Alert>
        )}
      </div>

      <hr />

      {/* Feature 2: Sync stock from sheet to server */}
      <div className="mb-3">
        <h6 className="fw-bold mb-2">
          <FontAwesomeIcon icon={faFileImport} className="me-2 text-success" />
          Sync Stock từ Sheet lên Server
        </h6>
        <p className="text-muted small mb-2">
          Nhập danh sách SKU, hệ thống sẽ đọc dữ liệu supplier stock từ sheet
          và cập nhật lên server.
        </p>
        <Form.Group className="mb-3">
          <Form.Label>Danh sách SKU (mỗi dòng 1 SKU)</Form.Label>
          <Form.Control
            as="textarea"
            rows={5}
            placeholder={"SKU-001\nSKU-002\nSKU-003"}
            value={skusText}
            onChange={(e) => setSkusText(e.target.value)}
          />
        </Form.Group>
        <Button
          variant="success"
          onClick={handleSync}
          disabled={syncing || !selectedSheet || !selectedSheetName}
        >
          {syncing ? (
            <>
              <Spinner size="sm" className="me-2" />
              Đang sync...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faFileImport} className="me-2" />
              Sync Stock
            </>
          )}
        </Button>

        {/* Sync Results Table */}
        {syncResults.length > 0 && (
          <Table striped hover size="sm" className="mt-3">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>SKU</th>
                <th>Kết quả</th>
                <th>Chi tiết</th>
              </tr>
            </thead>
            <tbody>
              {syncResults.map((r, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>
                    <code>{r.sku}</code>
                  </td>
                  <td>{getStatusBadge(r.status)}</td>
                  <td>
                    {r.supplierStock &&
                      r.supplierStock.map((s, j) => (
                        <Badge
                          key={j}
                          bg={s.status === "instock" ? "success" : "danger"}
                          className="me-1"
                        >
                          {s.status === "instock" ? "Có hàng" : "Hết hàng"}
                          {s.note && ` (${s.note})`}
                        </Badge>
                      ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default SyncStockTab;
